import combinate from 'combinate';
import { create } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import type { Campaign, ProductBundle, ProductWithBundleId } from '~/models/CampaignModel';
import { ProductType } from '~/models/product/ProductType';
import { SelectionMode } from '~/models/SelectionModel';
import { calculateSavingsFirstYear } from '~/store/basket/basket.helpers';
import { BYOD_PRODUCT_ID, REMOTE_INSTALLATION_PRODUCT_KEY } from '~/store/campaign/byod.constants';
import type { AuthToken, FormValues } from '../campaign/verification/vipps/api';
import { defaultDwellingUnit, defaultPartnerAlias } from './constants';
import type { CustomerInfoFields } from './form.utils';
import { SanityPartnerConfiguration } from '~/models/customer-filter/CustomerFilter';
import { captureMessage } from '@sentry/react';
import { trackToggleOpenBasketEvent } from '~/analytics/eventTracker';

// TODO: refactor basket functions into modular files and unit test
// TODO: action for checkout?

export type BasketFields = {
  useBasketV2: boolean;
  partnerAlias: string;
  dwellingUnit: string;
  selectedProducts: ProductWithBundleId[];
  isSummaryOpen: boolean;
  bundleVisibility: Record<string, { isVisible: boolean; isOpen: boolean }>;
  activeBundleId: Partial<Record<ProductType, string>>;
  selectedISP: SanityPartnerConfiguration | undefined;
  selectedVerificationProvider: string;
  postVerificationRedirect: string | undefined;
  verificationResponse:
    | {
        authResp: AuthToken;
        formValues: Partial<FormValues>;
      }
    | undefined;
  contactInfo: Record<CustomerInfoFields, string> | undefined;
  stepError: string | undefined;
  useDelayedActivation: boolean;
  /** Format: yyyy-mm-dd */
  delayedActivationDate: string | undefined;
};
type BasketStore = BasketFields & {
  // Actions
  toggleSummary: () => void;
  selectProduct: (product: ProductWithBundleId, bundle: ProductBundle<ProductWithBundleId>) => void;
  openBundle: (bundleId: string, isOpen: boolean) => void;
  showBundle: (bundleId: string, isVisible: boolean) => void;
  setActiveBundle: (productType: ProductType, bundleId: string) => void;
  setStepError: (error: string | undefined) => void;
  // Internett
  setIspPartner: (partner: SanityPartnerConfiguration | undefined) => void;
  // Verification
  setVerificationProvider: (provider: string) => void;
  setPostVerificationRedirect: (url: string) => void;
  setVerificationResponse: (response: { authResp: AuthToken; formValues: Partial<FormValues> }) => void;
  resetVerification: () => void;
  // Reset basket
  resetBasketForCampaign: (campaign: Campaign<ProductWithBundleId>) => void;
  resetBasketAfterCheckout: (data?: Partial<BasketFields>) => void;
};

// Mapped type of BasketStore that removes all functions
const defaultValues: BasketFields = {
  useBasketV2: false,
  dwellingUnit: defaultDwellingUnit,
  partnerAlias: defaultPartnerAlias,
  selectedProducts: [],
  isSummaryOpen: false,
  bundleVisibility: {},
  activeBundleId: {},
  selectedVerificationProvider: '',
  contactInfo: undefined,
  postVerificationRedirect: undefined,
  verificationResponse: undefined,
  selectedISP: undefined,
  stepError: undefined,
  useDelayedActivation: false,
  delayedActivationDate: undefined,
};

export const useBasket = create<BasketStore>()(
  devtools(
    persist(
      (set) => ({
        ...defaultValues,
        // Actions
        toggleSummary: () =>
          set(
            (state) => {
              trackToggleOpenBasketEvent(!state.isSummaryOpen);
              return { isSummaryOpen: !state.isSummaryOpen };
            },
            undefined,
            'toggle-summary',
          ),
        selectProduct: (product, bundle) =>
          set((state) => ({ selectedProducts: selectProduct(product, bundle, state.selectedProducts) }), undefined, 'select-product'),
        setActiveBundle: (productType, bundleId) =>
          set((state) => ({ activeBundleId: { ...state.activeBundleId, [productType]: bundleId } }), undefined, 'set-active-bundle'),
        openBundle: (bundleId, isOpen) =>
          set(
            (state) => ({
              bundleVisibility: {
                ...state.bundleVisibility,
                [bundleId]: { ...state.bundleVisibility[bundleId], isOpen },
              },
            }),
            undefined,
            'open-bundle',
          ),
        showBundle: (bundleId, isVisible) =>
          set(
            (state) => ({
              bundleVisibility: {
                ...state.bundleVisibility,
                [bundleId]: { ...state.bundleVisibility[bundleId], isVisible },
              },
            }),
            undefined,
            'show-bundle',
          ),
        setVerificationProvider: (provider) => set({ selectedVerificationProvider: provider }, undefined, 'set-verification-provider'),
        setIspPartner: (partner) => set({ selectedISP: partner }, undefined, 'set-isp-partner'),
        setPostVerificationRedirect: (url) => set({ postVerificationRedirect: url }, undefined, 'set-post-verification-redirect'),
        setVerificationResponse: (response) => set({ verificationResponse: response }, undefined, 'set-verification-response'),
        resetVerification: () =>
          set(
            {
              verificationResponse: undefined,
              selectedVerificationProvider: '',
              postVerificationRedirect: undefined,
            },
            undefined,
            'reset-verification',
          ),
        resetBasketForCampaign: (campaign) =>
          set(
            (state) => {
              const sub = campaign.products.subscription[0].offers[0];
              // Basket already configured for this campaign, return current state
              if (state.selectedProducts.some((sp) => sp.offer.commercialProductId === sub.offer.commercialProductId)) {
                return state;
              }
              // TODO: CHECK IF THIS IS STILL NEEDED
              const preChosenProducts = (Object.values(campaign.products) as ProductWithBundleId[][]).flat().filter((p) => p.isPreChosen);
              // Reset basket with selected subscription/campaign as base
              return {
                ...defaultValues,
                useBasketV2: true,
                selectedProducts: [sub, campaign.products.shipment[0].offers[0], ...preChosenProducts],
              };
            },
            undefined,
            'reset-basket',
          ),
        setStepError: (error) => set({ stepError: error }, undefined, 'set-step-error'),
        resetBasketAfterCheckout: (data = {}) => set({ ...defaultValues, ...data }, undefined, 'reset-basket-after-checkout'),
      }),
      {
        name: 'rtv-zustand-basket',
        storage: createJSONStorage(() => sessionStorage),
        partialize: ({ activeBundleId, bundleVisibility, ...rest }) => ({ ...rest }),
        onRehydrateStorage(_stateBeforeHydrate) {
          return (stateAfterHydrate, error) => {
            if (error) {
              console.warn('an error happened during hydration', error);
            } else {
              console.debug('hydration finished', stateAfterHydrate);
            }
          };
        },
      },
    ),
    {
      name: `zustand: ${globalThis.location?.origin}`,
      store: 'basket',
      enabled: true,
    },
  ),
);

export const selectProduct = (
  product: ProductWithBundleId,
  bundle: ProductBundle<ProductWithBundleId>,
  currentlySelectedProducts: ProductWithBundleId[],
): ProductWithBundleId[] => {
  const selectionConfig = bundle.selectionConfig;
  // Toggle existing product
  const existingProduct = currentlySelectedProducts.find((p) => p.id === product.id);
  if (existingProduct) {
    // Special case for BestDiscount, since we might have to select a better offer for some other product that is already in basket
    if (selectionConfig.selectionMode === SelectionMode.BestDiscount) {
      // if only one selected in bundle, simply remove it - best selection only works inside a bundle
      if (currentlySelectedProducts.filter((p) => p.bundleId === product.bundleId).length === 1) {
        return currentlySelectedProducts.filter((p) => p.id !== product.id);
      }
      return getBestDiscountCombination(product, currentlySelectedProducts, bundle);
    }
    return currentlySelectedProducts.filter((p) => p.id !== product.id);
  }

  /**
   * SingleSelection: Remove all other products in same bundle
   */
  if (selectionConfig.selectionMode === SelectionMode.Single) {
    // When BYOD is selected: remove all receivers and remote-installation product.
    if (product.id === BYOD_PRODUCT_ID) {
      return [
        ...currentlySelectedProducts
          .filter((sp) => sp.productType !== ProductType.Receiver)
          .filter((sp) => sp.productKey !== REMOTE_INSTALLATION_PRODUCT_KEY),
        product,
      ];
    }
    return [...currentlySelectedProducts.filter((sp) => sp.bundleId !== product.bundleId), product];
  }
  /**
   * Remove potentially conflicting products from current selection
   */
  const conflictingProductIds = product.displayData.conflictingProducts?.map((cp) => Number(cp.iccProductId)) || [];
  const filteredCurrentlySelectedProducts = currentlySelectedProducts.filter(
    (sp) => !conflictingProductIds.includes(sp.offer.commercialProductId),
  );

  /**
   * MultipleSelection: Do not add if max items have been reached in the bundle
   */
  if (selectionConfig.selectionMode === SelectionMode.Multiple) {
    const selectedInBundle = filteredCurrentlySelectedProducts.filter((sp) => sp.bundleId === product.bundleId).length;
    // Max items is 0 means no limit (?? is here for unit-tests so that can define partial objects)
    const maxItems = (selectionConfig.maxItems ?? 0) ? selectionConfig.maxItems : Number.MAX_SAFE_INTEGER;
    if (selectedInBundle >= maxItems) {
      // TODO: notify user?
      return filteredCurrentlySelectedProducts;
    }
  }
  /**
   * BestDiscount: Select the best offer from the selected products
   */
  if (selectionConfig.selectionMode === SelectionMode.BestDiscount) {
    return getBestDiscountCombination(product, filteredCurrentlySelectedProducts, bundle);
  }

  return [...filteredCurrentlySelectedProducts, product];
};

/**
 * Within a product bundle selects the offer that gives the best discount
 */
const getBestDiscountCombination = (
  product: ProductWithBundleId,
  selectedProducts: ProductWithBundleId[],
  bundle: ProductBundle<ProductWithBundleId>,
) => {
  // Prepare list of ids to combine and compare for best discount
  let productIdsInBundle = selectedProducts.filter((sp) => sp.bundleId === bundle.id).map((sp) => sp.offer.commercialProductId);

  // if "product" is already selected, remove it (toggle off), otherwise add it
  if (productIdsInBundle.includes(product.offer.commercialProductId)) {
    productIdsInBundle = productIdsInBundle.filter((id) => id !== product.offer.commercialProductId);
  } else {
    productIdsInBundle.push(product.offer.commercialProductId);
  }

  // 1. Get available offers for each product with estimated savings
  const availableOffers = productIdsInBundle.reduce(
    (acc, id) => {
      acc[id] = bundle.offers
        .filter((o) => o.offer.commercialProductId === id)
        .map((p) => ({
          product: p,
          savings: calculateSavingsFirstYear(p.pricing),
        }));
      return acc;
    },
    {} as Record<string, { product: ProductWithBundleId; savings: number }[]>,
  );

  // 2. Create combinations of all offers
  const combinations = (combinate(availableOffers) || []).map((combination) => ({
    products: Object.values(combination).map((v) => v.product),
    totalSavings: Object.values(combination).reduce((acc, v) => acc + v.savings, 0),
    discountedItems: Object.values(combination).filter((v) => v.savings > 0).length,
  }));

  // 3. Filter out combinations that have more discounted items than allowed
  const filteredCombinations = combinations.filter((c) => c.discountedItems <= bundle.selectionConfig.maxDiscountedItems);
  // Ensure there is a selectable combination
  if (filteredCombinations.length === 0) {
    console.error(new Error('No valid combinations found for best discount selection'));
    captureMessage('No valid combinations found for best discount selection', {
      level: 'warning',
      extra: { bundle: bundle.title, product, selectedProducts },
    });
    // TODO: notify user that product was not added?
    // Keep current selection, do nothing
    return selectedProducts;
  }
  // 4. Select the combination with the highest savings
  filteredCombinations.sort((a, b) => b.totalSavings - a.totalSavings);

  // return best combination plus all other already selected products
  return selectedProducts.filter((sp) => sp.bundleId !== bundle.id).concat(filteredCombinations[0].products);
};
